import axios from 'axios';

let authToken = null;

/** Called from AuthContext when the Supabase session changes. */
export function setAuthToken(token) {
  authToken = token;
}

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err.response?.status === 401 && authToken) {
      authToken = null;
      const { getSupabase } = await import('../lib/supabase');
      await getSupabase()?.auth.signOut();
    }
    return Promise.reject(err);
  }
);

// GCP Projects
export const getGcpProjects = () => api.get('/gcp-projects');
export const uploadServiceAccount = (formData) =>
  api.post('/gcp-projects/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const addGcpProjectByADC = (projectId, projectName) =>
  api.post('/gcp-projects/add', { projectId, projectName });
export const deleteGcpProject = (id) => api.delete(`/gcp-projects/${id}`);
export const getCloudRunsForProject = (projectId) =>
  api.get(`/gcp-projects/${projectId}/cloud-runs`);

// Products
export const getProducts = () => api.get('/products');
export const getProduct = (id) => api.get(`/products/${id}`);
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);

// Cloud Run details
export const getCloudRunInfo = (projectId, serviceName, region) =>
  api.get('/cloud-runs/info', { params: { projectId, serviceName, region } });

export const getCloudRunLogs = (projectId, serviceName, region, options = {}) =>
  api.get('/cloud-runs/logs', {
    timeout: 300000,
    params: {
      projectId,
      serviceName,
      region,
      startTime: options.startTime || undefined,
      endTime: options.endTime || undefined,
      severity: options.severity?.length > 0 ? options.severity.join(',') : undefined,
      fetchAll: 'true',
      maxLogs: options.maxLogs || 10000,
    },
  });

export const getCloudRunJobLogs = (projectId, jobName, region, options = {}) =>
  api.get('/vm/cloud-run-jobs/logs', {
    timeout: 300000,
    params: {
      projectId,
      jobName,
      region,
      startTime: options.startTime || undefined,
      endTime: options.endTime || undefined,
      severity: options.severity?.length > 0 ? options.severity.join(',') : undefined,
      executionName: options.executionName || undefined,
      fetchAll: 'true',
      maxLogs: options.maxLogs || 1000,
    },
  });

export const getCloudRunMetrics = (projectId, serviceName, region, options = {}) =>
  api.get('/cloud-runs/metrics', {
    params: {
      projectId,
      serviceName,
      region,
      startTime: options.startTime || undefined,
      endTime: options.endTime || undefined,
    },
  });

export const getCloudRunCosts = (projectId, serviceNames, serviceRegions, options = {}) =>
  api.get('/cloud-runs/costs', {
    timeout: 120000,
    params: {
      projectId,
      serviceNames: serviceNames.join(','),
      serviceRegions: serviceRegions.join(','),
      month: options.month || undefined,
    },
  });

export const getCloudRunTraces = (projectId, serviceNames, options = {}) =>
  api.get('/cloud-runs/traces', {
    timeout: 60000,
    params: {
      projectId,
      serviceNames: serviceNames.join(','),
      startTime: options.startTime || undefined,
      endTime: options.endTime || undefined,
      pageSize: options.pageSize || 50,
    },
  });

export const getServiceScaling = (projectId, serviceNames, region) =>
  api.get('/cloud-runs/scaling', {
    timeout: 120000,
    params: { projectId, serviceNames: serviceNames.join(','), region },
  });

// VM Observability
export const getVMInstances = (projectId, zone) =>
  api.get('/vm/instances', { params: { projectId, zone } });

export const getVMMetrics = (projectId, instanceId, zone, options = {}) =>
  api.get('/vm/metrics', {
    timeout: 120000,
    params: {
      projectId,
      instanceId,
      zone,
      startTime: options.startTime || undefined,
      endTime: options.endTime || undefined,
    },
  });

// Backup — GCS Buckets
export const getBuckets = (projectId) =>
  api.get('/vm/buckets', { params: { projectId } });

export const getBucketLatestFile = (projectId, bucketName, prefix) =>
  api.get('/vm/bucket-latest', { params: { projectId, bucketName, prefix: prefix || undefined } });

// Backup — Disk Snapshots
export const getDiskSnapshots = (projectId, diskName) =>
  api.get('/vm/snapshots', { params: { projectId, diskName: diskName || undefined } });

// Mongo Observability Config (persist VM/bucket selections)
export const getObservabilityConfig = (projectId) =>
  api.get('/vm/observability-config', { params: { projectId } });

export const saveObservabilityConfig = (projectId, config) =>
  api.put('/vm/observability-config', { projectId, ...config });

// Cloud Scheduler
export const getSchedulerJobs = (projectId) =>
  api.get('/vm/scheduler-jobs', { timeout: 120000, params: { projectId } });

// Cloud Run Jobs
export const getCloudRunJobs = (projectId) =>
  api.get('/vm/cloud-run-jobs', { timeout: 120000, params: { projectId } });

// Cloud Tasks
export const getTaskQueues = (projectId) =>
  api.get('/vm/task-queues', { timeout: 120000, params: { projectId } });

// All VM Instances
export const getAllVMInstances = (projectId) =>
  api.get('/vm/all-instances', { timeout: 120000, params: { projectId } });

// Cloud Run Services
export const getCloudRunServicesList = (projectId) =>
  api.get('/vm/cloud-run-services', { timeout: 120000, params: { projectId } });

// Cloud Build Triggers
export const getCloudBuildTriggers = (projectId) =>
  api.get('/vm/cloud-build-triggers', { timeout: 120000, params: { projectId } });

export const getCloudBuildTriggersLight = (projectId) =>
  api.get('/vm/cloud-build-triggers', { timeout: 30000, params: { projectId, light: 'true' } });

// GPU Info
export const getGpuInfo = () =>
  api.get('/vm/gpu-info', { timeout: 180000 });

export const downloadGpuInfoExcel = (data) =>
  api.post('/vm/gpu-info/download-excel', { data }, {
    timeout: 60000, responseType: 'blob',
  });

// VPN Details
export const getVpnDetails = (projectId) =>
  api.get('/vpn', { params: { projectId } });

export const addVpnDetail = (data) =>
  api.post('/vpn', data);

export const deleteVpnDetail = (id) =>
  api.delete(`/vpn/${id}`);

export const revealVpnPassword = (id, password) =>
  api.post(`/vpn/${id}/reveal`, { password });

export const downloadVpnConfig = (id) =>
  api.get(`/vpn/${id}/config`, { responseType: 'blob' });

// GCP Audit — config
export const getAllAuditConfigs = () =>
  api.get('/report/configs');

export const getAuditConfig = (projectId) =>
  api.get('/report/config', { params: { projectId } });

export const saveAuditConfig = (projectId, config) =>
  api.put('/report/config', { projectId, ...config });

export const deleteAuditConfig = (projectId) =>
  api.delete(`/report/config/${projectId}`);

// GCP Audit — generate & reports
export const generateReport = (startDate, endDate) =>
  api.post('/report/generate', { startDate, endDate }, { timeout: 300000 });

export const listAuditReports = () =>
  api.get('/report/reports');

export const getAuditReport = (id) =>
  api.get(`/report/reports/${id}`);

export const deleteAuditReport = (id) =>
  api.delete(`/report/reports/${id}`);

export const downloadReportExcel = (reports, startDate, endDate) =>
  api.post('/report/download-excel', { reports, startDate, endDate }, {
    timeout: 180000,
    responseType: 'blob',
  });

// GCP Audit — cron
export const getCronStatus = () =>
  api.get('/report/cron/status');

export const triggerCronReport = (type) =>
  api.post('/report/cron/trigger', { reportType: type }, { timeout: 300000 });

// Service Accounts
export const getServiceAccounts = (projectId) =>
  api.get('/vm/service-accounts', { timeout: 120000, params: { projectId } });

// Pub/Sub Topics
export const getPubSubTopics = (projectId) =>
  api.get('/vm/pubsub-topics', { timeout: 120000, params: { projectId } });

// Cost Estimation
export const estimateServiceCosts = (projectId, serviceType, serviceNames) =>
  api.post('/vm/estimate-costs', { projectId, serviceType, serviceNames }, { timeout: 120000 });

// Cloud Run Service Costs (usage-based)
export const getCloudRunServiceCosts = (projectId) =>
  api.get('/vm/cloud-run-service-costs', { timeout: 120000, params: { projectId } });

// Service Tags
export const getServiceTags = (projectId) =>
  api.get('/vm/service-tags', { params: { projectId } });

export const setServiceTag = (projectId, serviceType, serviceName, tag) =>
  api.put('/vm/service-tags', { projectId, serviceType, serviceName, tag });

export const removeServiceTag = (projectId, serviceType, serviceName) =>
  api.delete('/vm/service-tags', { data: { projectId, serviceType, serviceName } });

// Team Members
export const getTeamMembers = () => api.get('/team-members');
export const getTeamMember = (id) => api.get(`/team-members/${id}`);
export const createTeamMember = (data) => api.post('/team-members', data);
export const updateTeamMember = (id, data) => api.put(`/team-members/${id}`, data);
export const deleteTeamMember = (id) => api.delete(`/team-members/${id}`);

// MongoDB Configurations
export const getMongoConfigs = () => api.get('/mongo-configs');
export const createMongoConfig = (data) => api.post('/mongo-configs', data);
export const updateMongoConfig = (id, data) => api.put(`/mongo-configs/${id}`, data);
export const deleteMongoConfig = (id) => api.delete(`/mongo-configs/${id}`);

export default api;
